import React, { useState, useEffect } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { useApp } from "@/context/AppContext";
import { SPECIALTIES } from "@/i18n/translations";
import { CITY_NAMES as CITIES } from "@/data/cities";
import api from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import SearchableSelect from "@/components/SearchableSelect";
import DocCapture from "@/components/DocCapture";
import { toast } from "sonner";
import { Eye, EyeOff, Mail } from "lucide-react";
import { trackLead, trackCompleteRegistration } from "@/components/MetaPixel";

/** Doctor signup — no card required anymore (Feb 2026 spec).
 *  Stripe collects a PaymentMethod later via the "Agregar método de pago"
 *  button on the dashboard (Checkout setup mode with the merchant's PMC). */
function RegisterForm({ onSwitch }) {
  const { t, login, lang } = useApp();
  const nav = useNavigate();
  const [sp] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [showPw, setShowPw] = useState(false);
  const [form, setForm] = useState({
    email: "", password: "", password_confirm: "",
    title: "Dr.", full_name: "", specialty: "", clinic_name: "",
    address: "", city: "Quito", phone: "", photo_url: "",
    id_type: "cedula", id_number: "", ruc: "",
    senescyt_number: "", diploma_url: "", diploma_filename: "",
    cedula_front_url: "", cedula_back_url: "",
    referral_code: ((sp.get("ref") || "").toUpperCase()) || (localStorage.getItem("agendik_ref") ? (() => { try { return (JSON.parse(localStorage.getItem("agendik_ref")).code || "").toUpperCase(); } catch { return ""; } })() : ""),
    accepted_terms: false,
  });
  const setF = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const trialCopy = lang === "fr"
    ? "30 jours gratuits, sans carte. À la fin de l'essai, ajoutez votre moyen de paiement pour continuer : $49/mois."
    : lang === "en"
    ? "30 days free, no card required. At the end of the trial, add your payment method to continue: $49/month."
    : "30 días gratis, sin tarjeta. Al final de la prueba, agregue su método de pago para continuar: $49/mes.";

  const canSubmit = form.accepted_terms && !loading
                    && form.password.length >= 8
                    && form.password === form.password_confirm;

  const submit = async () => {
    if (!form.full_name || !form.specialty || !form.city || !form.senescyt_number) {
      return toast.error(t.doctorAuth.errRequired);
    }
    if (!form.id_number) return toast.error(t.doctorAuth.errIdRequired);
    if (form.id_type === "cedula" && !/^\d{10}$/.test(form.id_number))
      return toast.error(t.doctorAuth.errCedulaFormat);
    if (form.ruc && !/^\d{13}$/.test(form.ruc)) return toast.error(t.doctorAuth.errRucFormat);
    if (!form.cedula_front_url || !form.cedula_back_url) return toast.error(t.doctorAuth.errCedulaRequired);
    if (!form.diploma_url) return toast.error(t.doctorAuth.errDiplomaRequired);
    if (form.password.length < 8) return toast.error(t.doctorAuth.errPwShort);
    if (form.password !== form.password_confirm) return toast.error(t.doctorAuth.errPwMismatch);
    if (!form.accepted_terms) return toast.error(t.doctorAuth.errAcceptTerms);
    // Meta Pixel — Lead event fires the moment the doctor commits to signing up.
    try { trackLead(); } catch { /* ignore */ }
    setLoading(true);
    try {
      const r = await api.post("/doctor/register", form);
      login(r.data.token, "doctor", r.data.doctor);
      try { trackCompleteRegistration(); } catch { /* ignore */ }
      toast.success(t.doctorAuth.registered);
      nav("/doctor/panel");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error");
    } finally { setLoading(false); }
  };

  return (
    <>
      <h1 className="text-2xl font-bold text-slate-900 mb-1">{t.doctorAuth.registerTitle}</h1>
      <div data-testid="trial-banner" className="mb-4 bg-emerald-50 border border-emerald-100 text-emerald-800 text-xs font-semibold rounded-lg px-3 py-2">
        ✓ {trialCopy}
      </div>
      <div className="space-y-3">
        <div className="grid grid-cols-[110px_1fr] gap-2">
          <div>
            <Label>&nbsp;</Label>
            <Select value={form.title} onValueChange={v => setF("title", v)}>
              <SelectTrigger data-testid="auth-title-select"><SelectValue/></SelectTrigger>
              <SelectContent>
                <SelectItem value="Dr.">Dr.</SelectItem>
                <SelectItem value="Dra.">Dra.</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>{t.doctorAuth.fullName} *</Label>
            <Input data-testid="auth-full-name" value={form.full_name} onChange={e => setF("full_name", e.target.value)}/>
          </div>
        </div>
        <div><Label>{t.doctorAuth.email} *</Label>
          <Input data-testid="auth-email" type="email" value={form.email} onChange={e => setF("email", e.target.value)}/></div>
        <div><Label>{t.doctorAuth.specialty} *</Label>
          <SearchableSelect value={form.specialty} onChange={v => setF("specialty", v)}
                            options={SPECIALTIES[lang] || SPECIALTIES.es}
                            placeholder={t.doctorAuth.specialtyPlaceholder}
                            testId="auth-specialty"/></div>
        <div><Label>{t.doctorAuth.city} *</Label>
          <SearchableSelect value={form.city} onChange={v => setF("city", v)}
                            options={CITIES}
                            placeholder={t.doctorAuth.cityPlaceholder}
                            testId="auth-city"/></div>
        <div><Label>{t.doctorAuth.clinicName}</Label>
          <Input data-testid="auth-clinic" value={form.clinic_name} onChange={e => setF("clinic_name", e.target.value)}/></div>
        <div><Label>{t.doctorAuth.address}</Label>
          <Input data-testid="auth-address" value={form.address} onChange={e => setF("address", e.target.value)}/></div>
        <div><Label>{t.doctorAuth.phone}</Label>
          <Input data-testid="auth-phone" value={form.phone} onChange={e => setF("phone", e.target.value)}/></div>
        {/* ID document — single title above both the type dropdown and the
            number field (Feb 2026 spec). Dropdown options follow the active
            language. Uploads below adapt to the selected id_type: cédula → 2
            uploads (front + back); passport → 1 upload (data page). */}
        <div className="pt-2">
          <Label data-testid="id-doc-title">{t.doctorAuth.idDocTitle} *</Label>
          <div className="grid grid-cols-[140px_1fr] gap-2 mt-1">
            <Select value={form.id_type} onValueChange={v => setF("id_type", v)}>
              <SelectTrigger data-testid="auth-id-type"><SelectValue/></SelectTrigger>
              <SelectContent>
                <SelectItem value="cedula">{t.doctorAuth.idTypeCedulaOpt}</SelectItem>
                <SelectItem value="pasaporte">{t.doctorAuth.idTypePasaporteOpt}</SelectItem>
              </SelectContent>
            </Select>
            <Input data-testid="auth-id-number"
                   placeholder={t.doctorAuth.idNumberPh}
                   value={form.id_number}
                   onChange={e => setF("id_number", e.target.value.replace(/\s+/g, ""))}/>
          </div>
        </div>
        <div><Label>{t.doctorAuth.rucOpt}</Label>
          <Input data-testid="auth-ruc" maxLength={13} value={form.ruc}
                 onChange={e => setF("ruc", e.target.value.replace(/\D/g,"").slice(0,13))}/></div>
        <div><Label>{t.doctorAuth.senescyt} *</Label>
          <Input data-testid="auth-senescyt" value={form.senescyt_number} onChange={e => setF("senescyt_number", e.target.value)}/></div>
        {form.id_type === "cedula" ? (
          <>
            <DocCapture testId="auth-cedula-front" label={t.doctorAuth.cedulaFront + " *"}
                        value={form.cedula_front_url}
                        onChange={(url) => setF("cedula_front_url", url)}/>
            <DocCapture testId="auth-cedula-back" label={t.doctorAuth.cedulaBack + " *"}
                        value={form.cedula_back_url}
                        onChange={(url) => setF("cedula_back_url", url)}/>
          </>
        ) : (
          <DocCapture key="passport" testId="auth-passport-page" label={t.doctorAuth.passportDataPage + " *"}
                      value={form.cedula_front_url}
                      onChange={(url) => { setF("cedula_front_url", url); setF("cedula_back_url", url); }}/>
        )}
        <DocCapture testId="auth-diploma" label={t.doctorAuth.diploma + " *"} allowPdf maxMB={10}
                    value={form.diploma_url} filename={form.diploma_filename}
                    onChange={(url, fname) => { setF("diploma_url", url); setF("diploma_filename", fname || ""); }}/>
        <div><Label>{t.doctorAuth.password} *</Label>
          <div className="relative">
            <Input data-testid="auth-password" type={showPw ? "text" : "password"}
                   value={form.password} onChange={e => setF("password", e.target.value)}/>
            <button type="button" onClick={() => setShowPw(!showPw)}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700">
              {showPw ? <EyeOff size={16}/> : <Eye size={16}/>}
            </button>
          </div>
        </div>
        <div><Label>{t.doctorAuth.passwordConfirm} *</Label>
          <Input data-testid="auth-password-confirm" type={showPw ? "text" : "password"}
                 value={form.password_confirm} onChange={e => setF("password_confirm", e.target.value)}/></div>
        <label className="flex items-start gap-2 text-xs text-slate-600 pt-1">
          <input type="checkbox" data-testid="accept-terms-checkbox" checked={form.accepted_terms}
                 onChange={e => setF("accepted_terms", e.target.checked)} className="mt-1"/>
          <span dangerouslySetInnerHTML={{ __html: t.doctorAuth.acceptTermsHtml }}/>
        </label>
      </div>
      <Button data-testid="auth-submit-btn" onClick={submit} disabled={!canSubmit}
              className="w-full mt-5 h-11 bg-blue-600 hover:bg-blue-700 font-semibold disabled:opacity-50">
        {t.doctorAuth.register}
      </Button>
      <button data-testid="auth-switch-btn" onClick={onSwitch}
              className="w-full text-center text-sm text-blue-600 hover:text-blue-800 mt-4 font-medium">
        {t.doctorAuth.switchLogin}
      </button>
    </>
  );
}

function LoginForm({ onSwitch }) {
  const { t, login } = useApp();
  const nav = useNavigate();
  const [mode, setMode] = useState("login"); // login | forgot
  const [loading, setLoading] = useState(false);
  const [showPw, setShowPw] = useState(false);
  const [form, setForm] = useState({ email: "", password: "" });
  const [resetEmail, setResetEmail] = useState("");
  const [resetSent, setResetSent] = useState(false);

  const submit = async () => {
    setLoading(true);
    try {
      const r = await api.post("/doctor/login", form);
      login(r.data.token, "doctor", r.data.doctor);
      toast.success("Bienvenido");
      nav("/doctor/panel");
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
    finally { setLoading(false); }
  };

  const requestReset = async () => {
    if (!resetEmail) return toast.error("Email requerido");
    setLoading(true);
    try {
      await api.post("/doctor/password/request-reset", { email: resetEmail });
      setResetSent(true);
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
    finally { setLoading(false); }
  };

  if (mode === "forgot") {
    return (
      <>
        <h1 className="text-2xl font-bold text-slate-900 mb-1">{t.doctorAuth.resetTitle}</h1>
        <p className="text-sm text-slate-500 mb-5">{t.doctorAuth.resetLead}</p>
        {resetSent ? (
          <div className="text-center py-4">
            <Mail size={40} className="text-emerald-600 mx-auto mb-2"/>
            <p data-testid="doctor-reset-sent" className="text-sm text-slate-700">{t.doctorAuth.resetSent}</p>
            <Button onClick={() => { setMode("login"); setResetSent(false); }}
                    className="mt-5 bg-blue-600 hover:bg-blue-700">{t.doctorAuth.resetBack}</Button>
          </div>
        ) : (
          <>
            <Label>{t.doctorAuth.resetEmailLbl}</Label>
            <Input data-testid="doctor-reset-email" type="email" value={resetEmail}
                   onChange={e => setResetEmail(e.target.value)}/>
            <Button data-testid="doctor-reset-submit" onClick={requestReset} disabled={loading}
                    className="w-full mt-4 bg-blue-600 hover:bg-blue-700">{t.doctorAuth.resetSubmit}</Button>
            <button onClick={() => setMode("login")}
                    className="w-full text-sm text-slate-500 hover:text-blue-600 mt-3">{t.doctorAuth.resetCancel}</button>
          </>
        )}
      </>
    );
  }

  return (
    <>
      <h1 className="text-2xl font-bold text-slate-900 mb-1">{t.doctorAuth.loginTitle}</h1>
      <p className="text-sm text-slate-500 mb-5">{t.doctorAuth.loginLead}</p>
      <div className="space-y-3">
        <div><Label>{t.doctorAuth.email}</Label>
          <Input data-testid="login-email" type="email" value={form.email} onChange={e => setForm({...form, email: e.target.value})}/></div>
        <div><Label>{t.doctorAuth.password}</Label>
          <div className="relative">
            <Input data-testid="login-password" type={showPw ? "text" : "password"} value={form.password}
                   onChange={e => setForm({...form, password: e.target.value})}/>
            <button type="button" onClick={() => setShowPw(!showPw)}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700">
              {showPw ? <EyeOff size={16}/> : <Eye size={16}/>}
            </button>
          </div>
        </div>
      </div>
      <Button data-testid="login-submit-btn" onClick={submit} disabled={loading}
              className="w-full mt-5 h-11 bg-blue-600 hover:bg-blue-700 font-semibold">
        {t.doctorAuth.login}
      </Button>
      <button data-testid="forgot-btn" onClick={() => setMode("forgot")}
              className="w-full text-sm text-blue-600 hover:text-blue-800 mt-4">
        {t.doctorAuth.forgot}
      </button>
      <button data-testid="switch-register-btn" onClick={onSwitch}
              className="w-full text-sm text-slate-500 hover:text-blue-600 mt-2">
        {t.doctorAuth.switchRegister}
      </button>
    </>
  );
}

export default function DoctorAuth({ mode: initialMode }) {
  const { token, role } = useApp();
  const nav = useNavigate();
  const [mode, setMode] = useState(initialMode || "login");

  useEffect(() => {
    if (token && role === "doctor") nav("/doctor/panel", { replace: true });
  }, [token, role, nav]);

  return (
    <div className="max-w-lg mx-auto px-4 py-12">
      <Card className="border-slate-200 shadow-sm"><CardContent className="p-6 sm:p-8">
        {mode === "login"
          ? <LoginForm onSwitch={() => setMode("register")}/>
          : <RegisterForm onSwitch={() => setMode("login")}/>}
      </CardContent></Card>
    </div>
  );
}
